import { useRef, useState } from 'react';

import { useIntersection } from '@mantine/hooks';

import { createFileRoute } from '@tanstack/react-router';

import {
  type ActiveFilter,
  ActiveFilters,
  Divider,
  FilterBar,
  Group,
  MultiSelectInputView,
  SearchInput,
  Stack,
  StandardLayoutView,
  StatusFilterChipsView,
} from '@optiro/ui';

import CreateProject from '@/components/Modals/CreateProject/CreateProject';
import { statusColors } from '@/components/ProjectStatusBadge';
import useProjects from '@/hooks/projects/useProjects';
import { useAccounts } from '@/hooks/useAccounts';
import { useCategories } from '@/hooks/useCategories';
import { useFilterState } from '@/hooks/useFilterState';
import { useHeaderLeftSlot } from '@/hooks/useHeaderLeftSlot/useHeaderLeftSlot';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import ProjectsList from '@/modules/Projects/ProjectsList';
import { ProjectStatus } from '@/types/types';

export const Route = createFileRoute('/app/projects/')({
  component: RouteComponent,
});

const STATUS_OPTIONS: { label: string; value: ProjectStatus; color: string }[] = [
  { label: 'Udkast', value: 'draft', color: statusColors.draft },
  { label: 'Afventer', value: 'pending', color: statusColors.pending },
  { label: 'Planlægning', value: 'planning', color: statusColors.planning },
  { label: 'Aktiv', value: 'active', color: statusColors.active },
  { label: 'Afsluttet', value: 'completed', color: statusColors.completed },
];

const DEFAULT_STATUSES: ProjectStatus[] = ['draft', 'pending', 'planning', 'active'];

const DEFAULTS = {
  search: '',
  statuses: DEFAULT_STATUSES,
  accountIds: [] as string[],
  categoryIds: [] as string[],
};

function RouteComponent() {
  const { values, patch, reset } = useFilterState({ defaults: DEFAULTS });
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const searchInputRef = useRef<HTMLInputElement>(null);
  const { data: accounts } = useAccounts();
  const { data: categories } = useCategories();

  useKeyboardShortcuts({
    onSearchFocus: () => searchInputRef.current?.focus(),
    onCreateProject: () => {
      const btn = document.querySelector(
        '[data-testid="create-project-button"]'
      ) as HTMLButtonElement;
      btn?.click();
    },
  });

  const accountOptions = accounts?.map((a) => ({ value: String(a.id), label: a.name })) ?? [];
  const categoryOptions = categories?.map((c) => ({ value: String(c.id), label: c.name })) ?? [];

  const {
    data: projects = [],
    isLoading,
    isError,
  } = useProjects({
    status: values.statuses.length > 0 ? values.statuses : undefined,
    search: debouncedSearch || undefined,
    manager_ids: values.accountIds.length > 0 ? values.accountIds : undefined,
    category_ids: values.categoryIds.length > 0 ? values.categoryIds : undefined,
  });

  const activeCount = values.accountIds.length + values.categoryIds.length;
  const isFiltered =
    activeCount > 0 ||
    !!values.search ||
    values.statuses.includes('completed') ||
    values.statuses.length !== DEFAULT_STATUSES.length;

  const pills: ActiveFilter[] = [
    ...values.accountIds.map((id) => ({
      key: `mgr-${id}`,
      label: `Leder: ${accountOptions.find((a) => a.value === id)?.label ?? id}`,
      onRemove: () => patch({ accountIds: values.accountIds.filter((v) => v !== id) }),
    })),
    ...values.categoryIds.map((id) => ({
      key: `cat-${id}`,
      label: `Kategori: ${categoryOptions.find((c) => c.value === id)?.label ?? id}`,
      onRemove: () => patch({ categoryIds: values.categoryIds.filter((v) => v !== id) }),
    })),
  ];

  const toolbar = (
    <Stack gap="xs">
      <FilterBar gap="xs" onClear={reset} hasActiveFilters={isFiltered} activeCount={activeCount}>
        <SearchInput
          ref={searchInputRef}
          collapsible
          placeholder="Søg projekter…"
          value={values.search}
          onChange={(v) => patch({ search: v })}
          onDebouncedChange={setDebouncedSearch}
          size="xs"
        />
        <MultiSelectInputView
          size="xs"
          placeholder="Leder"
          data={accountOptions}
          value={values.accountIds}
          onChange={(v) => patch({ accountIds: v })}
          w={160}
          nothingFoundMessage="Ingen ledere"
        />
        <MultiSelectInputView
          size="xs"
          placeholder="Kategori"
          data={categoryOptions}
          value={values.categoryIds}
          onChange={(v) => patch({ categoryIds: v })}
          w={160}
          nothingFoundMessage="Ingen kategorier"
        />
      </FilterBar>

      <StatusFilterChipsView
        options={STATUS_OPTIONS}
        value={values.statuses}
        onChange={(v) => patch({ statuses: v as ProjectStatus[] })}
      />

      <ActiveFilters
        filters={pills}
        onClearAll={() => patch({ accountIds: [], categoryIds: [] })}
      />
    </Stack>
  );

  // ── Compact clone — follows into the app header's left slot once the page toolbar scrolls away ──
  const compactToolbar = (
    <Group gap="xs" wrap="nowrap">
      <SearchInput
        collapsible
        placeholder="Søg projekter…"
        value={values.search}
        onChange={(v) => patch({ search: v })}
        onDebouncedChange={setDebouncedSearch}
        size="xs"
      />

      <Divider orientation="vertical" />

      <MultiSelectInputView
        size="xs"
        placeholder="Leder"
        data={accountOptions}
        value={values.accountIds}
        onChange={(v) => patch({ accountIds: v })}
        w={130}
        nothingFoundMessage="Ingen ledere"
      />
      <MultiSelectInputView
        size="xs"
        placeholder="Kategori"
        data={categoryOptions}
        value={values.categoryIds}
        onChange={(v) => patch({ categoryIds: v })}
        w={130}
        nothingFoundMessage="Ingen kategorier"
      />
    </Group>
  );

  const { ref: toolbarSentinelRef, entry: toolbarEntry } = useIntersection({ threshold: 0 });
  const toolbarScrolledPast = toolbarEntry
    ? !toolbarEntry.isIntersecting && toolbarEntry.boundingClientRect.top < 0
    : false;

  useHeaderLeftSlot(toolbarScrolledPast ? compactToolbar : null);

  return (
    <StandardLayoutView
      title="Projekter"
      actions={<CreateProject />}
      toolbar={<div ref={toolbarSentinelRef}>{toolbar}</div>}
    >
      <ProjectsList projects={projects} isLoading={isLoading} isError={isError} />
    </StandardLayoutView>
  );
}
